// admin-shares.js - admin curation panel for FEATURED shared runs.
// Loaded by admin.js for role=admin. Uses GET /api/shares (admin sees all) and
// POST /api/shares/{token}/feature.
//
// A featured share is shown on the PUBLIC app landing as honest social proof
// (real run count + the share's GTI verify badge). The server only permits
// featuring a `plugin:std:` target - the free, anonymous-safe, no-JWT-runnable
// class. This panel mirrors that boundary in the UI: the Feature toggle is
// disabled for any non-`plugin:std:` target so an operator is never offered an
// action the server would reject. The server gate is the real boundary; this is
// cosmetic reinforcement.

import { api } from './api.js';

const STATE = { items: [] };

function esc(s) {
  if (s == null) return '';
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// Only free std plugins are anon-safe to feature (matches the server gate).
function isFeatureable(target) {
  return typeof target === 'string' && target.indexOf('plugin:std:') === 0;
}

function statusCell(s) {
  if (s.revoked) return '<span style="color:#b91c1c;font-weight:600;">revoked</span>';
  if (s.expired) return '<span style="color:#b45309;font-weight:600;">expired</span>';
  return '<span style="color:#047857;font-weight:600;">live</span>';
}

async function refresh() {
  const list = document.getElementById('admin-shares-list');
  if (!list) return;
  list.innerHTML = '<div style="padding:24px;text-align:center;color:var(--text-dim,#94a3b8);">Loading shares...</div>';
  try {
    const res = await api.listShares();
    // The admin List endpoint returns { shares: [...] }.
    STATE.items = (res && res.shares) || [];
    // Sort by RUNS desc by default so the genuinely most-run candidates - the
    // honest ones worth promoting - surface at the top of the operator's view.
    STATE.items.sort((a, b) => (b.runs || 0) - (a.runs || 0));
    renderList();
  } catch (e) {
    list.innerHTML = `<div style="padding:24px;text-align:center;color:#dc2626;">Could not load shares (${esc(e.message || e)}).</div>`;
  }
}

function renderList() {
  const list = document.getElementById('admin-shares-list');
  if (!list) return;
  if (!STATE.items.length) {
    list.innerHTML = '<div style="padding:24px;text-align:center;color:var(--text-dim,#94a3b8);">No shared links yet. Mint a share from a std plugin result, then feature it here.</div>';
    return;
  }
  const rows = STATE.items.map(s => {
    const featureable = isFeatureable(s.target);
    const featured = !!s.featured;
    // A non-plugin:std: target can never be featured to anonymous visitors, so the
    // toggle is disabled with a tooltip that states the same reason the server does.
    const toggleTitle = featureable
      ? (featured ? 'Remove from the public landing' : 'Feature on the public landing')
      : 'only free standard-plugin shares are anon-safe to feature';
    const toggleLabel = featured ? '<i class="fas fa-star"></i> Featured' : '<i class="far fa-star"></i> Feature';
    const toggleStyle = featured
      ? 'color:#b45309;border-color:#b45309;'
      : (featureable ? 'color:#047857;border-color:#047857;' : 'opacity:0.45;cursor:not-allowed;');
    const orderInput = featureable
      ? `<input type="number" class="shares-order" data-token="${esc(s.token)}" value="${Number(s.featuredOrder) || 0}" min="0" title="Landing display order (ascending, 0 first)" style="width:56px;padding:3px 6px;margin-left:6px;">`
      : '';
    return `
      <tr>
        <td style="font-family:monospace;font-size:11px;">${esc(s.target)}</td>
        <td>${esc(s.label) || '<em style="color:var(--text-dim,#94a3b8);">no label</em>'}</td>
        <td style="text-align:right;">${Number(s.clicks) || 0}</td>
        <td style="text-align:right;font-weight:700;">${Number(s.runs) || 0}</td>
        <td>${statusCell(s)}</td>
        <td style="white-space:nowrap;">
          <button class="btn btn-sm shares-toggle" data-token="${esc(s.token)}" data-featured="${featured ? '1' : '0'}"
                  ${featureable ? '' : 'disabled'} title="${esc(toggleTitle)}" style="${toggleStyle}">${toggleLabel}</button>
          ${orderInput}
        </td>
      </tr>`;
  }).join('');
  list.innerHTML = `
    <table class="admin-table" style="width:100%;">
      <thead>
        <tr><th>Target</th><th>Label</th><th style="text-align:right;">Clicks</th><th style="text-align:right;">Runs</th><th>Status</th><th>Featured</th></tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>`;
  wireRows();
}

function wireRows() {
  const list = document.getElementById('admin-shares-list');
  if (!list) return;
  list.querySelectorAll('.shares-toggle').forEach(btn => {
    btn.addEventListener('click', async () => {
      if (btn.disabled) return;
      const token = btn.dataset.token;
      const nowFeatured = btn.dataset.featured === '1';
      // Read the sibling order input so a feature+order change applies together.
      const orderEl = list.querySelector(`.shares-order[data-token="${CSS && CSS.escape ? CSS.escape(token) : token}"]`);
      const order = orderEl ? (parseInt(orderEl.value, 10) || 0) : 0;
      btn.disabled = true;
      const res = await api.featureShare(token, !nowFeatured, order);
      if (res && (res.featured !== undefined || res.token)) { await refresh(); }
      else { alert((res && res.error) || 'Could not update featured state.'); btn.disabled = false; }
    });
  });
  // Persist an order edit immediately for an already-featured row (so the operator
  // does not have to un/re-feature just to reorder).
  list.querySelectorAll('.shares-order').forEach(inp => {
    inp.addEventListener('change', async () => {
      const token = inp.dataset.token;
      const row = STATE.items.find(x => x.token === token);
      if (!row || !row.featured) return;   // only meaningful once featured
      const order = parseInt(inp.value, 10) || 0;
      const res = await api.featureShare(token, true, order);
      if (!(res && (res.featured !== undefined || res.token))) alert((res && res.error) || 'Could not update order.');
    });
  });
}

export function initAdminShares() {
  const btn = document.getElementById('btn-shares-refresh');
  if (btn) btn.addEventListener('click', refresh);
  // Load when the Featured subtab is opened (the panel is hidden at boot; the
  // generic .atab handler reveals it).
  const tab = document.querySelector('.atab[data-atab="shares"]');
  if (tab) tab.addEventListener('click', () => setTimeout(refresh, 50));
}

export default { initAdminShares };
